export type ErrorOptions<Context extends Jsonable = Jsonable, RequiredContext extends boolean = false> = {
  /**
   * The original cause of the error.
   *
   * It is used when catching and re-throwing an error with a more-specific
   * or useful error message in order to still have access to the original error (and stack).
   */
  cause?: Error | undefined
} & (RequiredContext extends true ? { context: Context } : { context?: Context | undefined })

export class BaseError<Context extends Jsonable = Jsonable, RequiredContext extends boolean = false> extends Error {
  public readonly context: RequiredContext extends true ? Context : Context | undefined

  constructor(
    ...args: RequiredContext extends true
      ? [message: string, options: ErrorOptions<Context, true>]
      : [message: string, options?: ErrorOptions<Context, false>]
  ) {
    const [message, options] = args
    const { cause, context } = options ?? {}

    super(message, { cause })
    this.name = this.constructor.name

    this.context = context as RequiredContext extends true ? Context : Context | undefined
  }
}

export class TechnicalError<
  Context extends Jsonable = Jsonable,
  RequiredContext extends boolean = false,
> extends BaseError<Context, RequiredContext> {}

export class AbortedError extends BaseError {}

/** Walks the cause chain so an abort rethrown with more context is still recognized as one. */
export function isAbortedError(value: unknown): boolean {
  return causeChainIncludes(value, AbortedError)
}

function causeChainIncludes<E extends Error>(
  value: unknown,
  errorClass: abstract new (...args: never[]) => E,
): boolean {
  const seen = new Set<Error>()
  let current: unknown = value
  while (current instanceof Error && !seen.has(current)) {
    if (current instanceof errorClass) return true
    seen.add(current)
    current = current.cause
  }
  return false
}

export class AssertionError extends TechnicalError {}

/**
 * This utility exception can be used to get a compile-time error based on whether a value is
 * never or not.
 * Inspired from workaround solutions of https://github.com/microsoft/TypeScript/issues/38881
 * Remove this when/if the issue is closed some day.
 *
 * /!\ Do not use on the client side when processing data received from the server as it
 * will not play nice with the server-side code.
 */
export class UnreachableCaseError extends TechnicalError {
  constructor(value: never, context: Jsonable = {}) {
    super('Unreachable code reached', { context: { value, context } })
  }
}

export class NotYetImplementedError extends TechnicalError {
  constructor() {
    super('Not yet implemented')
  }
}

export class IdempotencyKeyViolationError extends TechnicalError {}

export class ClientOutdatedError extends BaseError {
  constructor() {
    super('Client version is outdated')
  }
}

/** This pod runs a release older than the data it read, e.g. during a rolling deploy: a retry reaches an up-to-date pod. */
export class ServerOutdatedError extends BaseError {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options)
  }
}

/** Walks the cause chain, as a worker wraps the error it crashed on. */
export function isServerOutdatedError(value: unknown): boolean {
  return causeChainIncludes(value, ServerOutdatedError)
}

export type Jsonable =
  | string
  | number
  | boolean
  | null
  | undefined
  | readonly Jsonable[]
  | JsonableObject
  | { toJSON(): Jsonable }

export type JsonableObject = { readonly [key: string]: Jsonable }

/**
 * Helper to assert that something is defined and not null, while providing a clear explanation
 * and context for error alerting when it's not the case.
 *
 * @param value - The value which should be defined and not null.
 *
 * @param explanation - A clear explanation of what happened in the case it happens
 * from a functional standpoint.
 *   - GOOD : "While sending the D-60 renewal email, we encountered a
 *             contract with mrpwSelected set to true but no mrpwQuote"
 *   - BAD : "mrpwQuote is undefined"
 *
 * @param context - Additional context for the error sent to the sentry error to help with debugging.
 *
 * @returns The value if it is defined and not null.
 */
export function assertDefinedAndNotNull<T>(
  value: T | undefined | null,
  explanation: string,
  context: JsonableObject,
): NonNullable<T> {
  if (value == null) {
    throw new TechnicalError(explanation, { context })
  }
  return value
}
